import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const TerritoryMap = () => {
  const [selectedState, setSelectedState] = useState('texas');

  const territories = {
    texas: {
      name: 'Texas',
      status: 'High Demand',
      openPositions: 25,
      avgJobValue: '$14,500',
      monthlyJobs: '20-30',
      competition: 'Moderate',
      seasonality: 'Year-round',
      specialties: ['Storm Damage', 'Asphalt Shingles', 'Metal Roofing'],
      cities: [
        { name: 'Austin', demand: 'High', openings: 8 },
        { name: 'Houston', demand: 'Very High', openings: 12 },
        { name: 'Dallas', demand: 'High', openings: 5 }
      ]
    },
    colorado: {
      name: 'Colorado',
      status: 'Growing Market',
      openPositions: 18,
      avgJobValue: '$16,200',
      monthlyJobs: '15-25',
      competition: 'Low',
      seasonality: 'Seasonal Peak',
      specialties: ['Hail Damage', 'Metal Roofing', 'Snow Guards'],
      cities: [
        { name: 'Denver', demand: 'High', openings: 10 },
        { name: 'Colorado Springs', demand: 'Moderate', openings: 5 },
        { name: 'Boulder', demand: 'High', openings: 3 }
      ]
    },
    arizona: {
      name: 'Arizona',
      status: 'Expanding',
      openPositions: 15,
      avgJobValue: '$13,800',
      monthlyJobs: '18-28',
      competition: 'Moderate',
      seasonality: 'Year-round',
      specialties: ['Tile Roofing', 'Heat Resistance', 'Solar Integration'],
      cities: [
        { name: 'Phoenix', demand: 'Very High', openings: 8 },
        { name: 'Tucson', demand: 'Moderate', openings: 4 },
        { name: 'Scottsdale', demand: 'High', openings: 3 }
      ]
    }
  };

  const getDemandColor = (demand) => {
    switch (demand) {
      case 'Very High': return 'text-error';
      case 'High': return 'text-warning';
      case 'Moderate': return 'text-primary';
      default: return 'text-text-secondary';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'High Demand': return 'bg-error/10 text-error';
      case 'Growing Market': return 'bg-warning/10 text-warning';
      case 'Expanding': return 'bg-primary/10 text-primary';
      default: return 'bg-surface text-text-secondary';
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center space-y-6 mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full">
            <Icon name="MapPin" size={16} className="text-primary mr-2" />
            <span className="text-sm font-medium text-primary">Territory Opportunities</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-jakarta font-bold text-text-primary">
            Market Opportunities by Region
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Explore available territories, market demand, and earning potential in different regions. 
            Find the perfect market for your roofing business expansion.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Territory Selection */}
          <div className="space-y-6">
            <h3 className="text-xl font-jakarta font-bold text-text-primary">Select Territory</h3>
            
            <div className="space-y-3">
              {Object.entries(territories)?.map(([key, territory]) => (
                <button
                  key={key}
                  onClick={() => setSelectedState(key)}
                  className={`w-full text-left p-4 rounded-lg border transition-all duration-300 ${
                    selectedState === key
                      ? 'border-primary bg-primary/10' :'border-border bg-white hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-jakarta font-semibold text-text-primary">{territory?.name}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(territory?.status)}`}>
                          {territory?.status}
                        </span>
                        <span className="text-sm text-text-secondary">{territory?.openPositions} openings</span>
                      </div>
                    </div>
                    <Icon 
                      name="ChevronRight" 
                      size={16} 
                      className={`transition-transform duration-300 ${
                        selectedState === key ? 'rotate-90 text-primary' : 'text-text-secondary'
                      }`} 
                    />
                  </div>
                </button>
              ))}
            </div>

            {/* Quick Apply */}
            <div className="bg-white rounded-xl p-6 border border-border">
              <h4 className="font-jakarta font-semibold text-text-primary mb-4">Ready to Apply?</h4>
              <p className="text-sm text-text-secondary mb-4">
                Submit your application for the selected territory and start your journey with OpenbooQ.
              </p>
              <Button variant="default" fullWidth>
                Apply for {territories?.[selectedState]?.name}
              </Button>
            </div>
          </div>

          {/* Territory Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Map Placeholder */}
            <div className="bg-white rounded-2xl shadow-trust p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-jakarta font-bold text-text-primary">
                  {territories?.[selectedState]?.name} Territory
                </h3>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(territories?.[selectedState]?.status)}`}>
                  {territories?.[selectedState]?.status}
                </span>
              </div>
              
              {/* Interactive Map */}
              <div className="relative bg-surface rounded-xl h-64 mb-6 overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={`${territories?.[selectedState]?.name} Territory Map`}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${territories?.[selectedState]?.name}&z=6&output=embed`}
                  className="rounded-xl"
                />
                
                {/* Overlay with market info */}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-trust">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPin" size={16} className="text-primary" />
                      <span className="font-medium text-text-primary">{territories?.[selectedState]?.openPositions} Open Positions</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="TrendingUp" size={16} className="text-success" />
                      <span className="text-sm text-text-secondary">High Growth Market</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Market Statistics */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-surface rounded-lg">
                  <div className="text-lg font-jakarta font-bold text-primary">{territories?.[selectedState]?.avgJobValue}</div>
                  <div className="text-sm text-text-secondary">Avg Job Value</div>
                </div>
                <div className="text-center p-4 bg-surface rounded-lg">
                  <div className="text-lg font-jakarta font-bold text-primary">{territories?.[selectedState]?.monthlyJobs}</div>
                  <div className="text-sm text-text-secondary">Jobs/Month</div>
                </div>
                <div className="text-center p-4 bg-surface rounded-lg">
                  <div className="text-lg font-jakarta font-bold text-primary">{territories?.[selectedState]?.competition}</div>
                  <div className="text-sm text-text-secondary">Competition</div>
                </div>
                <div className="text-center p-4 bg-surface rounded-lg">
                  <div className="text-lg font-jakarta font-bold text-primary">{territories?.[selectedState]?.seasonality}</div>
                  <div className="text-sm text-text-secondary">Season</div>
                </div>
              </div>
            </div>

            {/* City Breakdown */}
            <div className="bg-white rounded-2xl shadow-trust p-8">
              <h4 className="text-lg font-jakarta font-bold text-text-primary mb-6">City Opportunities</h4>
              
              <div className="space-y-4">
                {territories?.[selectedState]?.cities?.map((city, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-surface rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                        <Icon name="Building" size={16} className="text-white" />
                      </div>
                      <div>
                        <h5 className="font-jakarta font-semibold text-text-primary">{city?.name}</h5>
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-medium ${getDemandColor(city?.demand)}`}>
                            {city?.demand} Demand
                          </span>
                          <span className="text-sm text-text-secondary">•</span>
                          <span className="text-sm text-text-secondary">{city?.openings} openings</span>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      View Details
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Specialties */}
            <div className="bg-white rounded-2xl shadow-trust p-8">
              <h4 className="text-lg font-jakarta font-bold text-text-primary mb-6">Market Specialties</h4>
              
              <div className="flex flex-wrap gap-3">
                {territories?.[selectedState]?.specialties?.map((specialty, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 bg-primary/10 text-primary font-medium rounded-full text-sm"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-surface rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Lightbulb" size={16} className="text-warning mt-0.5" />
                  <div>
                    <h5 className="font-medium text-text-primary mb-1">Market Insight</h5>
                    <p className="text-sm text-text-secondary">
                      {territories?.[selectedState]?.name} shows strong demand for {territories?.[selectedState]?.specialties?.[0]?.toLowerCase()} 
                      projects with consistent year-round opportunities and competitive pricing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TerritoryMap;